import { memo } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board } from '@air/api/types';

import { registerAssetModalEscKeyHandler } from '~/components/AssetModalV2/hooks/useAssetModalEscKeyHandler';
import { SharePrivateAssetModal } from '~/components/SharePrivateAssetModal/SharePrivateAssetModal';
import { type AssetShareLinkModalClip } from '~/components/SharePrivateClipModal/types';
import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';

export type SharePrivateClipModalProps = AirModalProps<{
  clip: AssetShareLinkModalClip;
  trackLocation: AirActionTrackingLocation;
  boardContext?: Board['id'] | undefined;
  analyticsLocation: PrivateAssetOpenLocation | null;
}>;

export const SharePrivateClipModal = memo(
  ({ analyticsLocation, clip, onClose, trackLocation }: SharePrivateClipModalProps) => {
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });

    // ShareLinkModal bypasses focus lock, so Escape is handled by the asset-modal owner
    // (focus stays on the Share trigger). Defer unregister so Reach Dialog's same-event
    // `onDismiss` cannot pop this handler and let comparison-exit run on that keydown.
    useIsomorphicLayoutEffect(() => {
      const unregister = registerAssetModalEscKeyHandler(onClose);

      return () => {
        queueMicrotask(unregister);
      };
    }, [onClose]);

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    return (
      <SharePrivateAssetModal
        analyticsLocation={analyticsLocation}
        clip={clip}
        onClose={onClose}
        trackLocation={trackLocation}
      />
    );
  },
);

SharePrivateClipModal.displayName = 'SharePrivateClipModal';
